import React, { useState, useEffect } from 'react';
import { Grid, FormControl, InputLabel, Select, MenuItem, Button, FormHelperText } from '@mui/material';
import { TaskFragment, TaskStatus } from '../../../generated/graphql';
import { FormInput } from '../../../components/form-input';
import { useApp } from '../../../components/app-context';
import { formatDate } from '@dwelvio/dateutils';
import { FormSelect } from '../../../components/form-select';
import { TaskStatusTypeLabelArray } from '../../../types';
import { Box, Stack } from '@mui/system';
import { TaskStatusChip } from '../../../components/task-status-chip';

interface FormFields {
  title: string;
  description?: string | null;
  status: TaskStatus;
  startDate?: Date;
  dueDate?: Date;
  unitId?: string;
  contractId?: string;
  completedAt?: Date;
}

interface TaskBasicInfoPanelProps {
  task: TaskFragment;
  onChange: (values: FormFields) => void;
  updateLoading: boolean;
  disabled?: boolean;
  properties?: {
    id: string;
    name: string;
  }[];
  contracts?: {
    id: string;
    title: string;
  }[];
}

export function TaskBasicInfoPanel(props: TaskBasicInfoPanelProps) {
  const { task, onChange, properties, contracts, disabled } = props;
  const app = useApp();
  const [fields, setFields] = useState<FormFields>({
    title: task.title,
    description: task.description ?? undefined,
    status: task.status,
    startDate: task.startDate ? new Date(task.startDate) : undefined,
    dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
    unitId: task.unitId ?? undefined,
    contractId: task.contractId ?? undefined,
    completedAt: task.completedAt ? new Date(task.completedAt) : undefined,
  });
  useEffect(() => {
    if (task) {
      setFields({
        title: task.title,
        description: task.description ?? undefined,
        status: task.status,
        startDate: task.startDate ? new Date(task.startDate) : undefined,
        dueDate: task.dueDate ? new Date(task.dueDate) : undefined,
        unitId: task.unitId ?? undefined,
        contractId: task.contractId ?? undefined,
        completedAt: task.completedAt ? new Date(task.completedAt) : undefined,
      });
    }
  }, [task]);

  if (!fields) {
    return null;
  }
  // Determine entity type based on current unitId/contractId
  const [entityType, setEntityType] = useState<'unit' | 'contract' | ''>(() => {
    if (fields.unitId) return 'unit';
    if (fields.contractId) return 'contract';
    return '';
  });

  const handleEntityTypeChange = (event: any) => {
    const newType = event.target.value as 'unit' | 'contract' | '';
    setEntityType(newType);

    // Clear both unit and contract when changing entity type
    setFields((s) => ({ ...s, unitId: undefined, contractId: undefined }));
  };

  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12 }}>
          <FormInput
            label="Názov"
            disabled={disabled}
            required
            value={fields.title}
            onChange={(e) => setFields((s) => ({ ...s, title: e.target.value }))}
          />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <FormInput
            label="Opis"
            disabled={disabled}
            value={fields.description}
            type={{ type: 'multiLine', minRows: 3, maxRows: 6 }}
            onChange={(e) => setFields((s) => ({ ...s, description: e.target.value }))}
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6 }}>
          <FormInput
            name="startDate"
            label="Dátum začatia"
            type="date"
            disabled={disabled}
            value={fields.startDate ? formatDate(fields.startDate, 'YMD') : ''} // force date format to YMD
            onChange={(e) =>
              setFields((s) => ({ ...s, startDate: e.target.value ? new Date(e.target.value) : undefined }))
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormInput
            name="dueDate"
            label="Termín splnenia"
            type="date"
            disabled={disabled}
            value={fields.dueDate ? formatDate(fields.dueDate, 'YMD') : ''} // force date format to YMD
            onChange={(e) =>
              setFields((s) => ({ ...s, dueDate: e.target.value ? new Date(e.target.value) : undefined }))
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          {/* <FormSelect
            name="status"
            label="Stav"
            value={fields.status}
            disabled={disabled}
            items={TaskStatusTypeLabelArray}
            onChange={(e) => setFields((s) => ({ ...s, status: e.target.value as TaskStatus }))}
          /> */}
          <FormControl required variant="standard" fullWidth>
            <InputLabel>Stav</InputLabel>
            <Select
              value={fields.status}
              label="Stav"
              onChange={(e) => setFields((s) => ({ ...s, status: e.target.value as TaskStatus }))}
              renderValue={(value) => {
                return <TaskStatusChip status={value as TaskStatus} />;
              }}
            >
              {TaskStatusTypeLabelArray.map((status) => (
                <MenuItem key={status.value} value={status.value}>
                  {status.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>Required</FormHelperText>
          </FormControl>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}></Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormControl fullWidth variant="standard">
            <InputLabel>Typ prepojenia</InputLabel>
            <Select
              name="entityType"
              label="Typ prepojenia"
              value={entityType}
              onChange={handleEntityTypeChange}
              disabled={disabled}
            >
              <MenuItem value="">Žiadne</MenuItem>
              <MenuItem value="unit">Nehnuteľnosť</MenuItem>
              <MenuItem value="contract">Prenájom</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {entityType === 'unit' && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth variant="standard">
              <InputLabel>Nehnuteľnosť</InputLabel>
              <Select
                name="unitId"
                label="Nehnuteľnosť"
                value={fields.unitId ?? ''}
                onChange={(e) => setFields((s) => ({ ...s, unitId: e.target.value as string }))}
                disabled={disabled}
              >
                <MenuItem value="">Žiadna</MenuItem>
                {properties?.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}

        {entityType === 'contract' && (
          <Grid size={{ xs: 12, sm: 6 }}>
            <FormControl fullWidth variant="standard">
              <InputLabel>Prenájom</InputLabel>
              <Select
                name="contractId"
                label="Prenájom"
                value={fields.contractId ?? ''}
                onChange={(e) => setFields((s) => ({ ...s, contractId: e.target.value as string }))}
                disabled={disabled}
              >
                <MenuItem value="">Žiadny</MenuItem>
                {contracts?.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        )}
      </Grid>
      <Box>
        <Button variant="contained" disabled={disabled || props.updateLoading} onClick={() => onChange(fields)}>
          Uložiť zmeny
        </Button>
      </Box>
    </Stack>
  );
}
